import {
  OVERDUE_HOURS_BOUNDS,
  RULE_MEANINGS,
  RULE_MEANING_LABELS,
  type RuleMeaning,
  type StageRule,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import { StageTagSwitch } from "@/modules/settings/screens/StageTagSwitch";

/**
 * Settings > Order stages (#465): which tag means what to an order's stage, and
 * when an unconfirmed COD order counts as overdue.
 *
 * The rules are data, matched ignoring case, so a renamed tag or a new tool
 * needs no rebuild. Tags seen on recent orders that no rule covers are listed
 * with "Use as…" and "Ignore", so a new confirmation tag is noticed instead of
 * silently doing nothing.
 */

const MEANING_OPTIONS = RULE_MEANINGS.map((value) => ({
  value,
  label: RULE_MEANING_LABELS[value],
}));

function MeaningSelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id?: string;
  value: RuleMeaning;
  onChange: (value: RuleMeaning) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        onChange(next as RuleMeaning);
      }}
    >
      <SelectTrigger id={id} className="w-52">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {MEANING_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Limit() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const inputId = useId();
  const settings = useQuery({
    queryKey: ["order-stages", "settings"],
    queryFn: () => data.stages.getSettings(),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (hours: number) =>
      data.stages.saveSettings({ overdueHours: hours }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        setSaved(true);
        setDraft(null);
        queryClient.setQueryData(["order-stages", "settings"], result.data);
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
      } else {
        setSaved(false);
        setRejection(result.rejection);
      }
    },
  });

  if (settings.isPending) return <TableSkeleton rows={1} />;
  if (settings.isError) {
    return (
      <LoadFailed
        message={settings.error.message}
        onRetry={() => {
          void settings.refetch();
        }}
      />
    );
  }
  const shown = draft ?? String(settings.data.overdueHours);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={inputId} className="text-sm font-medium">
        Flag a COD order as overdue after (hours)
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={inputId}
          inputMode="numeric"
          className="w-24"
          value={shown}
          disabled={save.isPending}
          onChange={(event) => {
            setDraft(event.target.value);
            setSaved(false);
          }}
        />
        <Button
          type="button"
          disabled={
            draft === null ||
            draft === String(settings.data.overdueHours) ||
            save.isPending
          }
          onClick={() => {
            setRejection(null);
            save.mutate(Number(draft));
          }}
        >
          {save.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
        {saved ? (
          <span
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            Saved.
          </span>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">
        From {OVERDUE_HOURS_BOUNDS.min} to {OVERDUE_HOURS_BOUNDS.max} hours.
        Counted from when the order was placed.
      </p>
      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}

function RuleRow({
  rule,
  onChanged,
}: {
  rule: StageRule;
  onChanged: () => void;
}) {
  const data = useDataSource();
  const [meaning, setMeaning] = useState<RuleMeaning>(rule.meaning);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const update = useMutation({
    mutationFn: (next: RuleMeaning) =>
      data.stages.updateRule(rule.id, { tag: rule.tag, meaning: next }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        onChanged();
      } else setRejection(result.rejection);
    },
  });
  const remove = useMutation({
    mutationFn: () => data.stages.deleteRule(rule.id),
    onSuccess: (result) => {
      if (result.ok) onChanged();
      else setRejection(result.rejection);
    },
  });

  return (
    <li className="flex flex-col gap-1 border-b py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 basis-56 text-sm break-words">
          {rule.tag}
        </code>
        <MeaningSelect
          value={meaning}
          disabled={update.isPending || remove.isPending}
          onChange={(next) => {
            setMeaning(next);
            update.mutate(next);
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove the rule for ${rule.tag}`}
          disabled={update.isPending || remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          <Trash2 aria-hidden />
        </Button>
      </div>
      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
            setMeaning(rule.meaning);
          }}
        />
      ) : null}
    </li>
  );
}

function NewRule({ onChanged }: { onChanged: () => void }) {
  const data = useDataSource();
  const tagId = useId();
  const [tag, setTag] = useState("");
  const [meaning, setMeaning] = useState<RuleMeaning>("confirmed_manual");
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const create = useMutation({
    mutationFn: () => data.stages.createRule({ tag, meaning }),
    onSuccess: (result) => {
      if (result.ok) {
        setTag("");
        setRejection(null);
        onChanged();
      } else setRejection(result.rejection);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={tagId} className="text-sm font-medium">
        Add a rule
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={tagId}
          className="w-64"
          placeholder="Tag exactly as it appears in Shopify"
          value={tag}
          onChange={(event) => {
            setTag(event.target.value);
          }}
        />
        <MeaningSelect value={meaning} onChange={setMeaning} />
        <Button
          type="button"
          disabled={tag.trim() === "" || create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          Add rule
        </Button>
      </div>
      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Unruled({ onChanged }: { onChanged: () => void }) {
  const data = useDataSource();
  const query = useQuery({
    queryKey: ["order-stages", "unruled"],
    queryFn: () => data.stages.unruledTags(),
  });
  const [picked, setPicked] = useState<Record<string, RuleMeaning>>({});
  const add = useMutation({
    mutationFn: (input: { tag: string; meaning: RuleMeaning }) =>
      data.stages.createRule(input),
    onSuccess: (result) => {
      if (result.ok) onChanged();
    },
  });

  if (!query.data || query.data.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 text-sm font-medium">Tags with no rule</h3>
      <p className="text-muted-foreground mb-2 text-xs">
        Seen on orders of the last 60 days. Choose what each means, or ignore it
        so it stops being listed.
      </p>
      <ul>
        {query.data.map((entry) => (
          <li
            key={entry.tag}
            className="flex flex-wrap items-center gap-2 border-b py-2 last:border-b-0"
          >
            <code className="min-w-0 flex-1 basis-48 text-sm break-words">
              {entry.tag}
            </code>
            <span className="text-muted-foreground text-xs">
              {entry.orders} orders
            </span>
            <MeaningSelect
              value={picked[entry.tag] ?? "confirmed_manual"}
              onChange={(next) => {
                setPicked((current) => ({ ...current, [entry.tag]: next }));
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={add.isPending}
              onClick={() => {
                add.mutate({
                  tag: entry.tag,
                  meaning: picked[entry.tag] ?? "confirmed_manual",
                });
              }}
            >
              Use as…
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={add.isPending}
              onClick={() => {
                add.mutate({ tag: entry.tag, meaning: "ignore" });
              }}
            >
              Ignore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OrderStages() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: ["order-stages", "rules"],
    queryFn: () => data.stages.listRules(),
  });

  function changed() {
    void queryClient.invalidateQueries({ queryKey: ["order-stages"] });
    // A rule moves the stage of every order that carries the tag.
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
  }

  if (rules.isPending) return <TableSkeleton rows={6} />;
  if (rules.isError) {
    return (
      <LoadFailed
        message={rules.error.message}
        onRetry={() => {
          void rules.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Order stages</CardTitle>
          <CardDescription>
            What a tag on an order means for its stage and its confirmation
            call. Changing a rule changes the stage of every order that carries
            the tag, at once. A tag with no rule is not used.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <Limit />
          <StageTagSwitch />
          <ul>
            {rules.data.map((rule) => (
              <RuleRow
                key={`${rule.id}-${rule.meaning}`}
                rule={rule}
                onChanged={changed}
              />
            ))}
          </ul>
          <NewRule onChanged={changed} />
          <Unruled onChanged={changed} />
        </CardContent>
      </Card>
    </div>
  );
}
